import describeResponseBody from '@client-shared/utils/describe-response-body.js'

// axios config for the list endpoints the server streams off a Mongo cursor
// (servsrc/lib/stream-json-list-response.js): GET plans, documents, pf-files
// and projects, POST activities.
//
// Once the first item is written the 200 head is committed, so a later server
// error can only tear the connection down - and Firefox or a proxy in between
// can still hand the cut-off body to the app as a complete 200. axios' default
// silent JSON parsing then returns the raw string, which crashed the callers'
// `data.map` ("a.map is not a function"). The transform below rejects such a
// body like any other failed request (error.response set), so the error
// interceptors report it to Raygun and the stores keep their previous list.
//
// Strict for a successful response only: axios runs the transform on error
// responses too, and their bodies need not be JSON (a 401 answers a plain-text
// "Unauthorized"). axios' own strict mode (responseType 'json' with
// transitional.silentJSONParsing false) replaced such a 401 with a parse error.
//
// Not an axios default: many endpoints answer a plain-text "OK"
// (res.sendStatus(200)).
// client-shared never imports axios (the apps inject it), so no AxiosError instance. The message
// leaves out the parser's: V8 quotes part of the input in it, and the body can hold user data.
const createInvalidBodyError = ({ config, response, data, reason }) => {
  const invalidBodyError = new Error(`Invalid JSON response body (${reason})`)

  invalidBodyError.code = 'ERR_BAD_RESPONSE'
  // The marker axios.isAxiosError() checks: the error interceptors, sendRaygun (request url and
  // status diagnostics) and the reconnect catch-up handle it like any other failed request
  invalidBodyError.isAxiosError = true
  invalidBodyError.config = config
  // sendRaygun reports the response data of an axios error, and this body is a cut-off payload of
  // ours - keep only its shape (see describe-response-body.js)
  invalidBodyError.response = {
    ...response,
    data: describeResponseBody(data),
  }

  return invalidBodyError
}

function parseJsonResponseBody (data, headers, status) {
  if (typeof data !== 'string') {
    return data
  }

  const isSuccessfulResponse = status >= 200 && status < 300
  // axios sets config.response while the transform runs (core/dispatchRequest.js)
  const invalidBodyErrorOptions = {
    config: this,
    response: this.response,
    data,
  }

  // An empty body is no list either: the stream writes at least its opening token, so an empty 2xx
  // body was cut off before it - passed on as is, a caller would read it as an empty list
  if (data === '') {
    if (isSuccessfulResponse) {
      throw createInvalidBodyError({
        ...invalidBodyErrorOptions,
        reason: 'empty',
      })
    }

    return data
  }

  try {
    return JSON.parse(data)
  } catch (error) {
    if (!isSuccessfulResponse) {
      return data
    }

    throw createInvalidBodyError({
      ...invalidBodyErrorOptions,
      reason: error.name,
    })
  }
}

const STREAMED_LIST_REQUEST_CONFIG = Object.freeze({
  transformResponse: Object.freeze([ parseJsonResponseBody ]),
})

export default STREAMED_LIST_REQUEST_CONFIG
